import React from 'react';

import type { MessageDescriptor } from 'react-intl';

import { PanelType } from '@atlaskit/adf-schema/panel';
import { blockTypeMessages } from '@atlaskit/editor-common/messages';
import {
	IconCustomPanel,
	IconPanel,
	IconPanelError,
	IconPanelNote,
	IconPanelSuccess,
	IconPanelWarning,
} from '@atlaskit/editor-common/quick-insert';
import {
	CUSTOM_PANEL_MENU_ITEM,
	ERROR_PANEL_MENU_ITEM,
	INFO_PANEL_MENU_ITEM,
	NOTE_PANEL_MENU_ITEM,
	STRUCTURE_SECTION,
	SUCCESS_PANEL_MENU_ITEM,
	WARNING_PANEL_MENU_ITEM,
} from '@atlaskit/editor-common/quick-insert/keys';
import { STRUCTURE_SECTION_RANK } from '@atlaskit/editor-common/quick-insert/rank';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { RegisterMenuItem } from '@atlaskit/editor-ui-control-model/types';

import type { PanelPlugin } from '../../panelPluginType';

import type { QuickInsertPanelType } from './PanelQuickInsertMenuItem';
import { PanelQuickInsertMenuItem } from './PanelQuickInsertMenuItem';

type PanelMenuItem =
	| typeof CUSTOM_PANEL_MENU_ITEM
	| typeof ERROR_PANEL_MENU_ITEM
	| typeof INFO_PANEL_MENU_ITEM
	| typeof NOTE_PANEL_MENU_ITEM
	| typeof SUCCESS_PANEL_MENU_ITEM
	| typeof WARNING_PANEL_MENU_ITEM;

type PanelItem = {
	icon: React.ComponentType;
	menuItem: PanelMenuItem;
	panelType: QuickInsertPanelType;
	title: MessageDescriptor;
};

const standardPanelItems: PanelItem[] = [
	{
		icon: IconPanel,
		menuItem: INFO_PANEL_MENU_ITEM,
		panelType: PanelType.INFO,
		title: blockTypeMessages.infoPanel,
	},
	{
		icon: IconPanelNote,
		menuItem: NOTE_PANEL_MENU_ITEM,
		panelType: PanelType.NOTE,
		title: blockTypeMessages.notePanel,
	},
	{
		icon: IconPanelSuccess,
		menuItem: SUCCESS_PANEL_MENU_ITEM,
		panelType: PanelType.SUCCESS,
		title: blockTypeMessages.successPanel,
	},
	{
		icon: IconPanelWarning,
		menuItem: WARNING_PANEL_MENU_ITEM,
		panelType: PanelType.WARNING,
		title: blockTypeMessages.warningPanel,
	},
	{
		icon: IconPanelError,
		menuItem: ERROR_PANEL_MENU_ITEM,
		panelType: PanelType.ERROR,
		title: blockTypeMessages.errorPanel,
	},
];

const customPanelItem: PanelItem = {
	icon: IconCustomPanel,
	menuItem: CUSTOM_PANEL_MENU_ITEM,
	panelType: PanelType.CUSTOM,
	title: blockTypeMessages.customPanel,
};

export const getPanelQuickInsertComponents = ({
	api,
	allowCustomPanel,
	allowCustomPanelEdit,
}: {
	allowCustomPanel: boolean;
	allowCustomPanelEdit: boolean;
	api: ExtractInjectionAPI<PanelPlugin> | undefined;
}): RegisterMenuItem[] => {
	const allPanelItems: PanelItem[] = [
		...standardPanelItems,
		...(allowCustomPanel && allowCustomPanelEdit ? [customPanelItem] : []),
	];

	return allPanelItems.map(({ icon, menuItem, panelType, title }) => ({
		key: menuItem.key,
		type: menuItem.type,
		parents: [
			{
				key: STRUCTURE_SECTION.key,
				type: STRUCTURE_SECTION.type,
				rank: STRUCTURE_SECTION_RANK[menuItem.key],
			},
		],
		component: () => (
			<PanelQuickInsertMenuItem api={api} icon={icon} panelType={panelType} title={title} />
		),
	}));
};
